import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { formatDateOnly } from "../lib/datetime.js";
import { PLATFORM_TIME_ZONE, todayInZone } from "../lib/zone.js";

interface ClientRow {
  id: string;
  name: string;
}

interface RateCardRow {
  id: string;
  client_id: string;
  effective_from: string;
  base_fee_cents: number;
  per_inbound_minute_cents: number;
  per_outbound_minute_cents: number;
  per_sms_cents: number;
  per_summary_cents: number;
  gst_rate: number;
}

function dollarsInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

interface RateDraft {
  effectiveFrom: string;
  baseFee: string;
  perInbound: string;
  perOutbound: string;
  perSms: string;
  perSummary: string;
  gstRate: string;
}

function draftFrom(r: RateCardRow): RateDraft {
  return {
    effectiveFrom: r.effective_from,
    baseFee: dollarsInput(r.base_fee_cents),
    perInbound: dollarsInput(r.per_inbound_minute_cents),
    perOutbound: dollarsInput(r.per_outbound_minute_cents),
    perSms: dollarsInput(r.per_sms_cents),
    perSummary: dollarsInput(r.per_summary_cents),
    gstRate: String(r.gst_rate),
  };
}

/** Dollars typed in a field -> whole cents, or null if it is not a non-negative number. */
function toCents(value: string): number | null {
  const n = Number(value);
  return value.trim() !== "" && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

export function RateCards() {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [clientId, setClientId] = useState("");
  const [rows, setRows] = useState<RateCardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [effectiveFrom, setEffectiveFrom] = useState(todayInZone(PLATFORM_TIME_ZONE));
  const [baseFee, setBaseFee] = useState("0");
  const [perInbound, setPerInbound] = useState("0");
  const [perOutbound, setPerOutbound] = useState("0");
  const [perSms, setPerSms] = useState("0");
  const [perSummary, setPerSummary] = useState("0");
  const [gstRate, setGstRate] = useState("0.10");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<RateDraft | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase
      .from("clients")
      .select("id, name")
      .order("name")
      .then(({ data }) => {
        const list = (data ?? []) as ClientRow[];
        setClients(list);
        if (list[0]) setClientId(list[0].id);
      });
  }, []);

  async function load() {
    if (!clientId) return;
    setLoading(true);
    const { data } = await supabase.from("rate_cards").select("*").eq("client_id", clientId).order("effective_from", { ascending: false });
    setRows((data ?? []) as RateCardRow[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!clientId) return;
    setError(null);
    const { error: err } = await supabase.from("rate_cards").insert({
      client_id: clientId,
      effective_from: effectiveFrom,
      base_fee_cents: Math.round(Number(baseFee) * 100),
      per_inbound_minute_cents: Math.round(Number(perInbound) * 100),
      per_outbound_minute_cents: Math.round(Number(perOutbound) * 100),
      per_sms_cents: Math.round(Number(perSms) * 100),
      per_summary_cents: Math.round(Number(perSummary) * 100),
      gst_rate: Number(gstRate),
    });
    if (err) {
      setError(err.message);
      return;
    }
    await load();
  }

  function startEdit(r: RateCardRow) {
    setError(null);
    setEditingId(r.id);
    setDraft(draftFrom(r));
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft(null);
    setError(null);
  }

  function patchDraft(patch: Partial<RateDraft>) {
    setDraft((d) => (d ? { ...d, ...patch } : d));
  }

  async function handleSave(r: RateCardRow) {
    if (!draft) return;
    const base = toCents(draft.baseFee);
    const inbound = toCents(draft.perInbound);
    const outbound = toCents(draft.perOutbound);
    const sms = toCents(draft.perSms);
    const summary = toCents(draft.perSummary);
    const gst = Number(draft.gstRate);
    if (base == null || inbound == null || outbound == null || sms == null || summary == null) {
      setError("Fees must be numbers of zero or more.");
      return;
    }
    if (!draft.effectiveFrom || !Number.isFinite(gst) || gst < 0 || gst > 1) {
      setError("Enter an effective-from date and a GST rate between 0 and 1 (for example 0.10).");
      return;
    }
    const today = todayInZone(PLATFORM_TIME_ZONE);
    if (
      (r.effective_from <= today || draft.effectiveFrom <= today) &&
      !window.confirm(
        "This rate card is already in effect. Changing it changes the rates used for every period that is not invoiced yet. " +
          "Periods already invoiced keep their invoices. Save the change?"
      )
    ) {
      return;
    }
    setError(null);
    setSaving(true);
    const { error: err } = await supabase
      .from("rate_cards")
      .update({
        effective_from: draft.effectiveFrom,
        base_fee_cents: base,
        per_inbound_minute_cents: inbound,
        per_outbound_minute_cents: outbound,
        per_sms_cents: sms,
        per_summary_cents: summary,
        gst_rate: gst,
      })
      .eq("id", r.id);
    setSaving(false);
    if (err) {
      setError(err.code === "23505" ? "This client already has a rate card with that effective-from date." : err.message);
      return;
    }
    cancelEdit();
    await load();
  }

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 16 }}>Rate cards</h1>
      <p style={{ color: "var(--rc-text-faint)", fontSize: 13, marginTop: -12, marginBottom: 16 }}>
        Billing uses the card in effect for the period being invoiced (fn_current_rate_card). To change rates from a date
        onward, add a new dated card. Edit a card only to correct it: periods already invoiced keep their invoices, but every
        period not invoiced yet uses the edited values.
      </p>

      <label style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
        Client
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={{ display: "block", padding: 8, marginTop: 4 }}>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ fontSize: 13 }}>
          Effective from
          <input type="date" required value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} style={fieldStyle} />
        </label>
        <label style={{ fontSize: 13 }}>
          Base fee ($/mo)
          <input type="number" step="0.01" value={baseFee} onChange={(e) => setBaseFee(e.target.value)} style={{ ...fieldStyle, width: 90 }} />
        </label>
        <label style={{ fontSize: 13 }}>
          Per inbound min ($)
          <input type="number" step="0.01" value={perInbound} onChange={(e) => setPerInbound(e.target.value)} style={{ ...fieldStyle, width: 90 }} />
        </label>
        <label style={{ fontSize: 13 }}>
          Per outbound min ($)
          <input type="number" step="0.01" value={perOutbound} onChange={(e) => setPerOutbound(e.target.value)} style={{ ...fieldStyle, width: 90 }} />
        </label>
        <label style={{ fontSize: 13 }}>
          Per SMS ($)
          <input type="number" step="0.01" value={perSms} onChange={(e) => setPerSms(e.target.value)} style={{ ...fieldStyle, width: 90 }} />
        </label>
        <label style={{ fontSize: 13 }}>
          Per summary ($)
          <input type="number" step="0.01" value={perSummary} onChange={(e) => setPerSummary(e.target.value)} style={{ ...fieldStyle, width: 90 }} />
        </label>
        <label style={{ fontSize: 13 }}>
          GST rate
          <input type="number" step="0.01" value={gstRate} onChange={(e) => setGstRate(e.target.value)} style={{ ...fieldStyle, width: 70 }} />
        </label>
        <button type="submit" style={{ padding: "8px 16px" }}>
          Add
        </button>
      </form>
      {error && <p style={{ color: "var(--rc-danger)" }}>{error}</p>}

      {loading ? (
        <p className="rc-loading">Loading...</p>
      ) : (
        <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: "10px 12px" }}>Effective from</th>
              <th style={{ padding: "10px 12px" }}>Base fee</th>
              <th style={{ padding: "10px 12px" }}>Inbound/min</th>
              <th style={{ padding: "10px 12px" }}>Outbound/min</th>
              <th style={{ padding: "10px 12px" }}>SMS</th>
              <th style={{ padding: "10px 12px" }}>Summary</th>
              <th style={{ padding: "10px 12px" }}>GST</th>
              <th style={{ padding: "10px 12px" }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) =>
              editingId === r.id && draft ? (
                <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)", background: "var(--rc-surface-sunken)" }}>
                  <td style={{ padding: "10px 12px" }}>
                    <input type="date" required value={draft.effectiveFrom} onChange={(e) => patchDraft({ effectiveFrom: e.target.value })} style={{ ...editField, width: 140 }} />
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <input type="number" step="0.01" min="0" value={draft.baseFee} onChange={(e) => patchDraft({ baseFee: e.target.value })} style={editField} />
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <input type="number" step="0.01" min="0" value={draft.perInbound} onChange={(e) => patchDraft({ perInbound: e.target.value })} style={editField} />
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <input type="number" step="0.01" min="0" value={draft.perOutbound} onChange={(e) => patchDraft({ perOutbound: e.target.value })} style={editField} />
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <input type="number" step="0.01" min="0" value={draft.perSms} onChange={(e) => patchDraft({ perSms: e.target.value })} style={editField} />
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <input type="number" step="0.01" min="0" value={draft.perSummary} onChange={(e) => patchDraft({ perSummary: e.target.value })} style={editField} />
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <input type="number" step="0.01" min="0" max="1" value={draft.gstRate} onChange={(e) => patchDraft({ gstRate: e.target.value })} style={{ ...editField, width: 64 }} />
                  </td>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                    <button onClick={() => void handleSave(r)} disabled={saving} style={{ ...linkBtn, fontWeight: 600 }}>
                      {saving ? "Saving..." : "Save"}
                    </button>
                    <button onClick={cancelEdit} disabled={saving} style={{ ...linkBtn, color: "var(--rc-text-muted)" }}>
                      Cancel
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                  <td style={{ padding: "10px 12px" }}>{formatDateOnly(r.effective_from)}</td>
                  <td style={{ padding: "10px 12px" }}>${dollarsInput(r.base_fee_cents)}</td>
                  <td style={{ padding: "10px 12px" }}>${dollarsInput(r.per_inbound_minute_cents)}</td>
                  <td style={{ padding: "10px 12px" }}>${dollarsInput(r.per_outbound_minute_cents)}</td>
                  <td style={{ padding: "10px 12px" }}>${dollarsInput(r.per_sms_cents)}</td>
                  <td style={{ padding: "10px 12px" }}>${dollarsInput(r.per_summary_cents)}</td>
                  <td style={{ padding: "10px 12px" }}>{(r.gst_rate * 100).toFixed(0)}%</td>
                  <td style={{ padding: "10px 12px" }}>
                    <button onClick={() => startEdit(r)} disabled={editingId !== null} style={linkBtn}>
                      Edit
                    </button>
                  </td>
                </tr>
              )
            )}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                  No rate cards yet for this client.
                </td>
              </tr>
            )}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

const fieldStyle: React.CSSProperties = { display: "block", padding: 6, marginTop: 4 };
const editField: React.CSSProperties = { padding: 4, width: 80, boxSizing: "border-box" };
const linkBtn: React.CSSProperties = { background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0, marginRight: 12, fontSize: 14 };
